import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Trash } from "lucide-react";

interface FileValidationProps {
  files: File[];
  isDarkMode: boolean;
  fileNameError?: string;
  showInlineSuccessMessage?: boolean;
  showFileUploadSuccessMessage?: boolean;
  onRemoveFile: (index: number) => void;
  truncateFileName: (fileName: string, maxLength?: number) => string;
  title?: string;
  successMessage?: string;
}

const FileValidation = ({
  files,
  isDarkMode,
  fileNameError,
  showInlineSuccessMessage = false,
  showFileUploadSuccessMessage = false,
  onRemoveFile,
  truncateFileName,
  title,
  successMessage
}: FileValidationProps) => {

  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  if (files.length === 0 && !showInlineSuccessMessage && !showFileUploadSuccessMessage) {
    return null;
  }

  const displayTitle = title || textConfig.FILE_VALIDATION_DEFAULT_TITLE;

  return (
    <>
      {files.length > 0 && (
        <Box
          mt={2}
          p={2}
          bgcolor={isDarkMode ? "#27272a" : "#f3f4f6"}
          borderRadius={2}
          mb={2}
        >
          <Typography
            color={isDarkMode ? "#fff" : "#000"}
            variant="body2"
            mb={1}
          >
            {displayTitle}
          </Typography>
          
          {fileNameError && (
            <Typography color="#FF3D3D" fontSize="12px" mb={1}>
              {fileNameError}
            </Typography>
          )}
          
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {files.map((file, index) => (
              <li
                key={index}
                className={`flex justify-between items-center mb-1 ${
                  isDarkMode ? "text-white" : "text-gray-800"
                } ${file.name.length > 150 ? "text-red-500" : ""}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4px",
                  padding: "4px 0"
                }}
              >
                <span style={{ flex: 1 }}>
                  {truncateFileName(file.name)} - {(file.size / 1024).toFixed(2)} {textConfig.FILE_VALIDATION_KB}
                  {file.name.length > 150 && (
                    <span style={{ color: "#FF3D3D", marginLeft: "4px" }}>
                      ({textConfig.FILE_VALIDATION_NAME_TOO_LONG})
                    </span>
                  )}
                </span>
                <Trash
                  size={16}
                  className="text-red-500 cursor-pointer hover:text-red-400"
                  style={{ 
                    color: "#EF4444",
                    cursor: "pointer",
                    marginLeft: "8px",
                    flexShrink: 0
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemoveFile(index);
                  }}
                />
              </li>
            ))}
          </ul>
        </Box>
      )}

      {(showInlineSuccessMessage || (showFileUploadSuccessMessage && successMessage)) && (
        <Box
          mt={2}
          p={2}
          borderRadius={1}
          bgcolor={isDarkMode ? "#27272A" : "#f0fdf4"}
          border="1px solid #22C55E"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Box
            sx={{
              color: "#22C55E",
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="#22C55E"
                strokeWidth="2"
              />
              <path
                d="M12 8V12"
                stroke="#22C55E"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="16" r="1" fill="#22C55E" />
            </svg>
          </Box>
          <Typography color="#22C55E">
            {showInlineSuccessMessage 
              ? textConfig.FILE_VALIDATION_COLLECTION_SUCCESS_MESSAGE
              : showFileUploadSuccessMessage && successMessage
              ? successMessage
              : textConfig.FILE_VALIDATION_DEFAULT_SUCCESS_MESSAGE
            }
          </Typography>
        </Box>
      )}
    </>
  );
};

export default FileValidation;